'use client';

import React, { useState } from 'react';

import { Rating } from '@bytel/product-wall';
import {
    Alignable,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Justifiable,
    Link,
    Progress,
    Section,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { reviewsServices } from '@services/reviews';

import { ReviewsInfoType } from '@app-types/review';

import { StatusState } from '@trilogy-ds/react/lib/objects/facets/Status';

import { UserReview } from './user-review';

export type ReviewProps = {
    title: string;
    product: string;
    reviews?: ReviewsInfoType;
};

export function Review({ title, product, reviews: _reviews }: ReviewProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [reviews, setReviews] = useState<ReviewsInfoType | undefined>(_reviews ?? undefined);
    const [canShowMore, setCanShowMore] = useState<boolean>(_reviews ? _reviews.reviewCount > 5 : false);

    if (!reviews) return null;

    const handleShowMoreReviews = () => {
        const nextOffset = offset + 5;
        setOffset(nextOffset);
        setIsLoading(true);
        reviewsServices.getReviews(product, nextOffset).then((_reviews) => {
            if (_reviews) {
                const newReviews = [...reviews.reviews, ..._reviews.reviews];
                setReviews({
                    ...reviews,
                    reviews: newReviews,
                });
                setCanShowMore(_reviews.reviewCount > newReviews.length);
            } else {
                setCanShowMore(false);
            }
            setIsLoading(false);
        });
    };

    return (
        <div id='reviews-section' className='ignore-sticky-scroll'>
            <Title level={TitleLevels.FOUR}>Les avis sur {title}</Title>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Columns>
                    <Column className='has-text-centered'>
                        <Section backgroundColor={TrilogyColor.BACKGROUND}>
                            <Title level={TitleLevels.FOUR}>Note moyenne général</Title>
                            <Title level={TitleLevels.TWO} className='is-marginless'>
                                {reviews.note}
                            </Title>
                            <View flexable justify={Justifiable.JUSTIFIED_CENTER}>
                                <Rating rating={reviews.note * 2} />
                            </View>
                            <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>{reviews.reviewCount} avis</Text>
                            <Link
                                href='/choisir-un-telephone/avis-client-authentique'
                                className='has-text-weight-semibold'
                            >
                                Conditions et authenticité des avis clients
                            </Link>
                        </Section>
                    </Column>
                    {reviews?.averages && Object.values(reviews.averages).length > 0 && (
                        <Column className='has-text-centered'>
                            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                                <Title level={TitleLevels.FOUR}>Note moyenne des clients</Title>
                                <>
                                    {Object.values(reviews.averages).map(({ label, note }) => (
                                        <Columns mobile verticalAlign={Alignable.ALIGNED_CENTER} key={label}>
                                            <Column size={5}>
                                                <Text level={TextLevels.TWO} marginless>
                                                    {label}
                                                </Text>
                                            </Column>
                                            <Column size={5}>
                                                <Progress status={StatusState.INFO} value={note} max={5} small />
                                            </Column>
                                            <Column size={2}>
                                                <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} marginless>
                                                    {note}
                                                </Text>
                                            </Column>
                                        </Columns>
                                    ))}
                                </>
                            </Section>
                        </Column>
                    )}
                </Columns>
                <Section backgroundColor={TrilogyColor.BACKGROUND}>
                    <>
                        {reviews?.reviews.map((review) => <UserReview review={review} key={review.user.login} />)}
                        {canShowMore && (
                            <>
                                <Spacer size={SpacerSize.SIX} />
                                <View flexable justify={Justifiable.JUSTIFIED_CENTER}>
                                    <Button
                                        variant={ButtonVariant.PRIMARY}
                                        onClick={handleShowMoreReviews}
                                        loading={isLoading}
                                    >
                                        Voir plus d&apos;avis
                                    </Button>
                                </View>
                            </>
                        )}
                    </>
                </Section>
            </Section>
        </div>
    );
}
